import type { RenderTooltipContentData } from '@shopify/polaris-viz';
import type { IRMetricKey } from '../../../../common/type';

import { SINGLE_CHART_COLOR, COMPARISON_CHART_COLOR } from '../../../constants';
import { useFilterByCampaignParams } from '~/presentation/reports/query/hooks';
import { getIncrease, IncreasePercentage } from '~/modules/analytics';
import { isReverseReportMetric } from '../../../../common/type';
import { InlineStack, Text } from '@shopify/polaris';
import { WrapperTooltip } from './WrapperTooltip';
import { formatPercent } from '../../../utils';

export interface PageTooltipProps {
  data: RenderTooltipContentData;
  formatValue: (value: number) => string;
  formatKeyByInterval?: (value: string | number | null, isExpandDetail?: boolean) => string;
  metricKey?: IRMetricKey;
}

export const PageTooltip: React.FC<PageTooltipProps> = ({
  data,
  formatValue,
  formatKeyByInterval,
  metricKey,
}) => {
  const { hasFilterByCampaign } = useFilterByCampaignParams();

  const { activeIndex } = data;
  const currentData = data?.dataSeries[0]?.data[activeIndex];
  const previousIndex = hasFilterByCampaign ? activeIndex - 1 : activeIndex;
  const previousSeriesIndex = hasFilterByCampaign ? 0 : 1;

  const previousData = data?.dataSeries[previousSeriesIndex]?.data[previousIndex];

  const value = Number(currentData.value || 0);
  const previousValue = Number(previousData?.value || 0);

  const currentNameForLineChart = formatKeyByInterval?.(currentData?.key, true) || currentData?.key;
  const previousNameForLineChart =
    formatKeyByInterval?.(previousData?.key, true) || previousData?.key;

  const currentName = !formatKeyByInterval ? data?.dataSeries[0]?.name : currentNameForLineChart;
  const previousName = !formatKeyByInterval ? data?.dataSeries[1]?.name : previousNameForLineChart;

  const title = data.formatters?.titleFormatter?.(data.title || '') || data.title;

  const increase = hasFilterByCampaign
    ? getIncrease(value, previousValue)
    : formatPercent(currentData);

  return (
    <WrapperTooltip title={title}>
      <InlineStack gap={'400'} align="space-between" blockAlign="center">
        <InlineStack gap={'100'} blockAlign="center">
          <div
            className="w-[8px] h-[8px] rounded-full"
            style={{
              backgroundColor: SINGLE_CHART_COLOR,
            }}
          />
          <Text as="p" variant="bodySm" fontWeight="medium" tone="subdued">
            {currentName}
          </Text>
        </InlineStack>
        <InlineStack blockAlign="center" gap="100">
          <Text as="span" variant="bodySm" fontWeight="semibold">
            {formatValue(currentData.value || 0)}
          </Text>
          <IncreasePercentage increase={increase} isReverse={isReverseReportMetric(metricKey)} />
        </InlineStack>
      </InlineStack>

      {!hasFilterByCampaign && (
        <InlineStack gap={'400'} align="space-between" blockAlign="center">
          <InlineStack gap={'100'} blockAlign="center">
            <div
              className="w-[8px] h-[8px] rounded-full"
              style={{
                backgroundColor: COMPARISON_CHART_COLOR,
              }}
            />
            <Text as="p" variant="bodySm" tone="subdued" fontWeight="medium">
              {previousName}
            </Text>
          </InlineStack>
          <InlineStack blockAlign="center" gap="100">
            <Text as="span" variant="bodySm" fontWeight="semibold">
              {formatValue(previousData.value || 0)}
            </Text>
            <div className="opacity-0">
              <IncreasePercentage
                increase={increase}
                isReverse={isReverseReportMetric(metricKey)}
              />
            </div>
          </InlineStack>
        </InlineStack>
      )}
    </WrapperTooltip>
  );
};
